import { Observable, Subject, ReplaySubject } from 'rxjs'
import { useState, useEffect, useCallback } from 'react'

/**
 * Subscribe to an RxJS observable on mount.
 *
 * @param initialValue the initial value
 * @param source the source observable
 * @param deps dependencies that should trigger a unsub->sub flow.
 */
export function useObservableStream<T>(
  initialValue: T,
  source: Observable<T> | (() => Observable<T>),
  deps: readonly any[] = []
) {
  const [value, setValue] = useState(initialValue)
  useEffect(() => {
    const source$ = typeof source === 'function' ? source() : source
    const subscription = source$.subscribe({
      next: setValue,
    })

    return () => {
      subscription.unsubscribe()
    }
  }, deps)

  return value
}

/**
 * Like `useObservableStream`, we just don't care about the value.
 * @param source
 * @param deps
 */
export function useObservableEffect<T>(
  source: Observable<T> | (() => Observable<T>),
  deps?: readonly any[]
) {
  useObservableCallback(source, undefined, deps)
}

/**
 * Like `useObservableStream`, we just don't care about the value, and invoke a
 * callback on every item emitted.
 *
 * @param source
 * @param next
 * @param deps
 */
export function useObservableCallback<T>(
  source: Observable<T> | (() => Observable<T> | undefined) | undefined,
  next: ((v: T) => unknown) | undefined,
  deps?: readonly any[]
) {
  deps = deps || []
  next = useCallback(next || (() => void 0), deps)
  useEffect(() => {
    const source$ = typeof source === 'function' ? source() : source
    const subscription = source$?.subscribe(next)
    return () => {
      subscription?.unsubscribe()
    }
  }, [next, ...deps])
}

/**
 * Uses an RxJS subject.
 */
export function useSubject<T>(subjectFactory = () => new Subject<T>()) {
  return useState(subjectFactory)[0]
}

/**
 * Returns an Rx Observable stream that emits a value whenever state changes, including the
 * initial state. Always emits the latest value on subscribe (Replay with buffer count = 1)
 *
 * @param stateValue
 */
export function useObservableFromState<T>(stateValue: T): Observable<T> {
  const subject = useSubject(() => new ReplaySubject<T>(1))
  useEffect(() => {
    subject.next(stateValue)
  }, [stateValue])
  return useState(() => subject.asObservable())[0]
}
